import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import GooglePlaces from '@synapsestudios/google-places-field';
import { fitBounds } from 'google-map-react/utils';
import { Modal } from 'synfrastructure';
import {
  getMovementSpecialistProfiles,
  getUserLocation,
  resetMovementSpecialistProfiles,
  setUserViewedMsIntroduction,
  setActiveMovementSpecialistId,
} from './redux/actions';
import { selectMovementSpecialists, selectActiveMovementSpecialist } from './redux/selectors';
import Icon from '../../../common/icons/Icon';
import GoogleMap from './GoogleMap';
import Loading from '../../../common/components/Loading';
import { sendIntercomEvent } from '../../../common/events';
import Pagination from 'rc-pagination';
import USLocale from 'rc-pagination/lib/locale/en_US';
import Profile from './MovementSpecialistProfile';
import 'rc-pagination/assets/index.css';

class MovementSpecialists extends Component {
  state = {
    cardHoverId: null,
    latitude: null,
    longitude: null,
    mapSize: { width: 640, height: 480 },
    mobileView: 'list',
    page: 1,
    resultsPerPage: 8,
    showMsIntroductionModal: false,
  };

  componentDidMount() {
    if (!this.props.user.getIn(['data', 'viewed_ms_intro'])) {
      this.setState({
        showMsIntroductionModal: true,
      });

      this.props.dispatch(setUserViewedMsIntroduction(this.props.user.getIn(['data', 'id'])));
    }

    if (!this.props.userLocation.loaded) {
      // Fetch newest movement specialists regardless of location by default
      this.submitSearch();
      this.props.dispatch(getUserLocation());
    } else {
      const { latitude, longitude } = this.props.userLocation;
      if (latitude && longitude) {
        this.setState(
          {
            latitude,
            longitude,
            mapCenter: { lat: latitude, lng: longitude },
            mapZoom: 12,
          },
          this.submitSearch
        );
      } else {
        this.submitSearch();
      }
    }

    sendIntercomEvent('viewed_movement_specialists');
  }

  componentDidUpdate(prevProps) {
    const { movementSpecialists } = this.props;
    if (!prevProps.userLocation.loaded && this.props.userLocation.loaded) {
      const { latitude, longitude } = this.props.userLocation;
      if (latitude && longitude) {
        this.setState(
          {
            latitude,
            longitude,
            mapCenter: { lat: latitude, lng: longitude },
            mapZoom: 12,
          },
          this.submitSearch
        );
      }
    }

    if (
      prevProps.movementSpecialists.data !== movementSpecialists.data &&
      (movementSpecialists.data && movementSpecialists.data.results.length === 0)
    ) {
      sendIntercomEvent('search_empty');
    }

    if (!prevProps.movementSpecialists.loaded && this.props.movementSpecialists.loaded) {
      this.centerMap();
    }
  }

  componentWillUnmount() {
    this.props.dispatch(resetMovementSpecialistProfiles());
  }

  setCardHover = id => {
    this.setState({ cardHoverId: id });
  };

  centerMap = size => {
    if (this.props.movementSpecialists.data && this.props.movementSpecialists.data.results.length) {
      const bounds = new window.google.maps.LatLngBounds();
      const results = this.props.movementSpecialists.data.results.map(
        result => new window.google.maps.LatLng(result.latitude, result.longitude)
      );

      results.forEach(n => bounds.extend(n));

      // extend bounds to add padding and fix bug where Maps API fitBounds
      // doesn't work when two points are the same lat or long
      bounds.extend(
        new window.google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01)
      );
      bounds.extend(
        new window.google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01)
      );

      const corners = {
        ne: bounds.getNorthEast().toJSON(),
        sw: bounds.getSouthWest().toJSON(),
      };

      const { center, zoom } = fitBounds(corners, size || this.state.mapSize);

      this.setState({
        mapCenter: center,
        mapZoom: zoom,
        mapSize: size || this.state.mapSize,
      });
    }
  };

  onMapChange = (zoom, size) => {
    if (size.height !== this.state.mapSize.height || size.width !== this.state.mapSize.width) {
      this.centerMap(size);
    } else {
      this.setState({
        mapZoom: zoom,
      });
    }
  };

  onSelectPlace = place => {
    this.setState(
      {
        latitude: place.latitude,
        longitude: place.longitude,
        page: 1,
      },
      this.submitSearch
    );
  };

  onSelectMovementSpecialist = ms => {
    this.props.dispatch(setActiveMovementSpecialistId(ms ? ms.id : null));
    if (ms) {
      window.scrollTo(0, 0);
    }
  };

  onClickMarker = ms => {
    this.setState({
      mapCenter: { lat: Number(ms.latitude), lng: Number(ms.longitude) },
    });
  };

  setPage = page => {
    this.setState({ page }, this.submitSearch);
  };

  submitSearch = e => {
    if (e) e.preventDefault();

    const { page, latitude, longitude, resultsPerPage } = this.state;
    const query = { page, resultsPerPage };

    if (latitude && longitude) {
      query.latitude = latitude;
      query.longitude = longitude;

      this.setState({
        mapCenter: { lat: latitude, lng: longitude },
        mapZoom: 12,
      });
    }

    this.props.dispatch(getMovementSpecialistProfiles(query));
  };

  toggleMsIntroductionModal = () => {
    this.setState(state => ({
      showMsIntroductionModal: !state.showMsIntroductionModal,
    }));
  };

  renderMovementSpecialistCards(movementSpecialists) {
    const { activeMovementSpecialist } = this.props;
    return movementSpecialists.map((ms, k) => (
      <div
        key={k}
        className={
          activeMovementSpecialist && activeMovementSpecialist.id === ms.id ? 'ms__card ms__card--active' : 'ms__card'
        }
        onClick={this.onSelectMovementSpecialist.bind(this, ms)}
        onMouseEnter={() => this.setCardHover(ms.id)}
        onMouseLeave={() => this.setCardHover(null)}
      >
        <div className="ms__image" style={{ backgroundImage: `url(${ms.profileImage.location})` }} />
        <div className="ms__info">
          <h3>{ms.name}</h3>
          <p>{ms.location_short}</p>
        </div>
      </div>
    ));
  }

  viewList = () => {
    this.setState(state => ({
      mobileView: 'list',
    }));
  };

  viewMap = () => {
    this.setState(state => ({
      mobileView: 'map',
    }));
  };

  renderSearchBar = () => {
    return (
      <form onSubmit={this.submitSearch}>
        <div className="ms__search">
          <Icon icon="MapMarker" />
          <GooglePlaces autoActivateFirstSuggest onSelect={this.onSelectPlace} placeholder="Search location" />
        </div>
      </form>
    );
  };

  renderMap = () => {
    const { movementSpecialists } = this.props;
    const { mapCenter, mapZoom } = this.state;
    return (
      <GoogleMap
        center={mapCenter}
        movementSpecialists={movementSpecialists.data ? movementSpecialists.data.results : []}
        defaultOptions={{ scrollwheel: false }}
        zoom={mapZoom}
        cardHoverId={this.state.cardHoverId}
        onClickViewProfile={this.onSelectMovementSpecialist}
        onClickMarker={this.onClickMarker}
        onChange={this.onMapChange}
      />
    );
  };

  renderProfile = () => {
    const { activeMovementSpecialist } = this.props;
    return (
      <Profile
        goBack={this.onSelectMovementSpecialist.bind(this, null)}
        movementSpecialist={activeMovementSpecialist}
        mobileView={this.state.mobileView}
      />
    );
  };

  renderMSInfoModal = () => {
    const { showMsIntroductionModal } = this.state;
    return (
      <Modal isVisible={showMsIntroductionModal} onClose={this.toggleMsIntroductionModal}>
        <div className="modal__content">
          <div className="ms__info-modal">
            <h3>Certified Movement Specialists</h3>
            <div className="ms__info-modal__content">
              <div
                className="ms__info-modal__img"
                style={{ backgroundImage: 'url(https://d27vybgvtr1dba.cloudfront.net/ms.jpg)' }}
              />
              <div className="ms__info-modal__about">
                <p>
                  Support your training by booking a hands-on Tathata Golf Follow-Up with a Certified Movement
                  Specialist.
                </p>
                <ul>
                  <li>
                    <Icon icon="CheckmarkCircle" />
                    1-on-1 Body Movement Support
                  </li>
                  <li>
                    <Icon icon="CheckmarkCircle" />
                    No matter your schedule, Movement Specialists are always available for you
                  </li>
                  <li>
                    <Icon icon="CheckmarkCircle" />
                    {"Book as many as you need; there's no limit to how much support you can get"}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="modal__actions">
          <button onClick={this.toggleMsIntroductionModal} className="button flex-end">
            Close
          </button>
        </div>
      </Modal>
    );
  };

  renderMovementSpecialists = movementSpecialists => {
    if (movementSpecialists.loading) {
      return <Loading />;
    }

    if (!movementSpecialists.data || !movementSpecialists.data.results || !movementSpecialists.data.results.length) {
      return (
        <div>
          No Movement Specialists found in the area at this time, but check out our{' '}
          <Link to="/subscription/coaching/online-follow-up">Online</Link> or{' '}
          <Link to="/subscription/coaching/in-person-follow-up">In-Person Tathata Follow-ups!</Link>
        </div>
      );
    }

    return (
      <div>
        {this.renderMovementSpecialistCards(movementSpecialists.data ? movementSpecialists.data.results : [])}
        {movementSpecialists.data.pagination.rowCount > movementSpecialists.data.pagination.pageSize && (
          <div className="ms-pagination">
            <Pagination
              onChange={this.setPage}
              current={this.state.page}
              pageSize={movementSpecialists.data.pagination.pageSize}
              total={movementSpecialists.data.pagination.rowCount}
              locale={USLocale}
            />
          </div>
        )}
      </div>
    );
  };

  renderMobile() {
    const { movementSpecialists, userLocation, activeMovementSpecialist } = this.props;
    const { mobileView } = this.state;

    return (
      <div className="ms__mobile">
        {userLocation.loading ? (
          <div className="ms__loading-page">
            <div className="ms__loading">
              <Loading />Looking up Movement Specialists in your area...
            </div>
          </div>
        ) : null}
        <div className="ms__list">
          <Link className="ms__back-to" to="/subscription/coaching">
            <span className="button__back-button">
              <Icon icon="BackArrow" />Back to Additional Coaching
            </span>
          </Link>
          <div className="ms__list-toggle">
            <button
              className="ms__list-toggle__button"
              onClick={() => this.viewList()}
              style={
                mobileView === 'list'
                  ? { backgroundColor: '#2F3031', border: '1px solid #e0e0e0', borderRadius: '2px' }
                  : null
              }
            >
              <div className="ms__list-toggle__button-children">
                <Icon icon="List" />List View
              </div>
            </button>
            <button
              className="ms__list-toggle__button"
              onClick={() => this.viewMap()}
              style={
                mobileView === 'map'
                  ? { backgroundColor: '#2F3031', border: '1px solid #e0e0e0', borderRadius: '2px' }
                  : null
              }
            >
              <div className="ms__list-toggle__button-children">
                <Icon icon="MapMarker" />Map View
              </div>
            </button>
          </div>
          {this.renderSearchBar()}
          {this.state.mobileView === 'list' ? (
            <div className="ms__list-results">
              {activeMovementSpecialist ? this.renderProfile() : this.renderMovementSpecialists(movementSpecialists)}
            </div>
          ) : null}
          {this.state.mobileView === 'map' ? (
            <div>{activeMovementSpecialist ? this.renderProfile() : this.renderMap()}</div>
          ) : null}
        </div>
      </div>
    );
  }

  render() {
    const { activeMovementSpecialist, movementSpecialists, userLocation, isMobile } = this.props;

    if (!movementSpecialists.firstLoadComplete) {
      return <Loading />;
    }

    if (isMobile) {
      return this.renderMobile();
    }

    return (
      <div className={activeMovementSpecialist ? 'ms__search-page--active-profile' : ''}>
        {userLocation.loading ? (
          <div className="ms__loading-page">
            <div className="ms__loading">
              <Loading />Looking up Movement Specialists in your area...
            </div>
          </div>
        ) : (
          this.renderMSInfoModal()
        )}
        <div className="ms__list">
          <Link className="ms__back-to" to="/subscription/coaching">
            <span className="button__back-button">
              <Icon icon="BackArrow" />Back to Additional Coaching
            </span>
          </Link>
          {this.renderSearchBar()}
          <div className="ms-learn-more__wrapper">
            <a className="button button--theme--outline ms-learn-more" onClick={this.toggleMsIntroductionModal}>
              Learn More About Movement Specialists
            </a>
          </div>
          <div className="ms__list-results">{this.renderMovementSpecialists(movementSpecialists)}</div>
        </div>
        {activeMovementSpecialist ? this.renderProfile() : this.renderMap()}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  isMobile: state.browser.lessThan.desktop,
  movementSpecialists: selectMovementSpecialists(state),
  user: state.subscription.user,
  activeMovementSpecialist: selectActiveMovementSpecialist(state),
  userLocation: state.subscription.movementSpecialists.userLocation,
});

export default connect(mapStateToProps)(MovementSpecialists);



// WEBPACK FOOTER //
// ./src/subscription/coaching/movement-specialists/MovementSpecialists.js